import { isTelemetryStale, telemetryAgeMs, type TelemetryKey, type VehicleState } from '@hgc/core';
import type { LinkStats } from '@hgc/link';
import type { StatusLevel } from '@hgc/ui';

import { ageText } from '../app/telemetry/format';

/**
 * 飞行分区的**纯数据层**：HUD、仪表、工具条、引导动作共用同一套"怎么读一份车辆状态"的规则。
 *
 * 三条贯穿全文件的规则，都是为了让这个界面不会骗人：
 *
 * 1. **`null` 不是 0。** 没有数据一律显示 `NO_DATA`，并且必须能说出"为什么没有"。
 *    把缺失画成 0 的姿态仪会告诉飞行员"飞机是平的" —— 那是这类界面最危险的失败方式。
 * 2. **新鲜度按子系统算**（`TELEMETRY_STALE_MS`），不看某个全局时间戳。姿态 1 秒、
 *    电池 15 秒、返航点 1 分钟；一个阈值只能表达三者中最悲观的那个。
 * 3. **过期是危险，不是提示。** 一个不再更新的读数看起来与实时读数一模一样，
 *    所以它必须被抬到 `caution` 以上并配上文字，而不是只把数字变灰。
 */

export const NO_DATA = '--';

const LEVEL_ORDER: readonly StatusLevel[] = ['neutral', 'info', 'ok', 'caution', 'warning', 'critical'];

/** 取更严重的等级。一个读数既是"过期"又是"电池告急"时，不能只显示成过期。 */
export function worstLevel(...levels: readonly StatusLevel[]): StatusLevel {
  let result: StatusLevel = 'neutral';
  for (const level of levels) {
    if (LEVEL_ORDER.indexOf(level) > LEVEL_ORDER.indexOf(result)) result = level;
  }
  return result;
}

export interface Freshness {
  /** 距上次更新多久；从未收到为 `null` */
  readonly ageMs: number | null;
  /** 从未收到也算过期 —— 对界面而言"没有数据"和"很久没更新"是同一件事：都不能当读数用 */
  readonly stale: boolean;
  readonly ageLabel: string;
}

export function freshnessOf(state: VehicleState, key: TelemetryKey, now: number): Freshness {
  const ageMs = telemetryAgeMs(state, key, now);
  return {
    ageMs,
    stale: isTelemetryStale(state, key, now),
    ageLabel: ageText(ageMs),
  };
}

export function fixed(value: number | null, digits: number): string {
  return value === null || Number.isNaN(value) ? NO_DATA : value.toFixed(digits);
}

/* ────────────────────────────── HUD ────────────────────────────── */

/** 顶部的一条关键读数 */
export interface HudReadout {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly level: StatusLevel;
  readonly stale: boolean;
  readonly ageLabel: string;
  readonly source: string;
}

interface NumericSpec {
  readonly id: string;
  readonly label: string;
  readonly unit: string;
  readonly source: string;
  /** 新鲜度按这一子系统算：地速与高度来自不同消息，共用一个时间戳会互相掩盖 */
  readonly key: TelemetryKey;
  readonly digits: number;
  /** `null` = 这一项没有可用值（没收到过，或收到的是哨兵值） */
  readonly read: (state: VehicleState) => number | null;
}

/** 地速：`VFR_HUD.groundspeed` 优先，退回到 `GLOBAL_POSITION_INT` 的 NED 分量合成 */
function groundSpeedMps(state: VehicleState): number | null {
  if (state.groundspeedMps !== null) return state.groundspeedMps;
  if (state.vxMps === null || state.vyMps === null) return null;
  return Math.hypot(state.vxMps, state.vyMps);
}

const NUMERIC_SPECS: readonly NumericSpec[] = [
  { id: 'groundspeed', label: '地速', unit: 'm/s', source: 'VFR_HUD', key: 'hud', digits: 1, read: groundSpeedMps },
  { id: 'airspeed', label: '空速', unit: 'm/s', source: 'VFR_HUD', key: 'hud', digits: 1, read: (s) => s.airspeedMps },
  {
    id: 'altitude',
    label: '相对高度',
    unit: 'm',
    source: 'GLOBAL_POSITION_INT',
    key: 'position',
    digits: 1,
    read: (s) => s.relativeAltM,
  },
  { id: 'climb', label: '爬升率', unit: 'm/s', source: 'VFR_HUD', key: 'hud', digits: 2, read: (s) => s.climbRateMps },
];

function readout(
  spec: {
    readonly id: string;
    readonly label: string;
    readonly unit: string;
    readonly source: string;
    readonly key: TelemetryKey;
  },
  value: string | null,
  level: StatusLevel,
  state: VehicleState,
  now: number,
): HudReadout {
  const freshness = freshnessOf(state, spec.key, now);
  const missing = value === null;
  return {
    id: spec.id,
    label: spec.label,
    value: value ?? NO_DATA,
    unit: missing ? '' : spec.unit,
    // 缺失与过期都必须显眼：`caution` 不足以表达"这个数字已经不能信了"，缺失用 `warning`
    level: freshness.stale ? worstLevel(level, missing ? 'warning' : 'caution') : level,
    stale: freshness.stale,
    ageLabel: freshness.ageLabel,
    source: spec.source,
  };
}

/**
 * 顶部关键读数：地速/空速、高度、爬升率、模式、解锁状态。
 *
 * 速度/高度/爬升率来自两条消息（`VFR_HUD` 与 `GLOBAL_POSITION_INT`），所以新鲜度分别算；
 * 模式与解锁状态是布尔/文本量，它们的"有没有数据"只能由心跳新鲜度表达。
 */
export function buildHudReadouts(state: VehicleState, now: number): readonly HudReadout[] {
  const numeric = NUMERIC_SPECS.map((spec) =>
    readout(spec, fixed(spec.read(state), spec.digits), 'neutral', state, now),
  );

  const heartbeatFresh = freshnessOf(state, 'heartbeat', now);
  const hasHeartbeat = state.lastHeartbeatAt !== null;

  return [
    ...numeric,
    readout(
      { id: 'mode', label: '模式', unit: '', source: 'HEARTBEAT', key: 'heartbeat' },
      state.modeName,
      'info',
      state,
      now,
    ),
    readout(
      { id: 'armed', label: '解锁状态', unit: '', source: 'HEARTBEAT', key: 'heartbeat' },
      hasHeartbeat ? (state.armed ? '已解锁' : '未解锁') : null,
      state.armed ? 'caution' : 'ok',
      state,
      now,
    ),
    // 心跳年龄本身也是一条读数：链路静默时它是唯一还在变的数字
    readout(
      { id: 'heartbeat', label: '心跳年龄', unit: 'ms', source: 'HEARTBEAT', key: 'heartbeat' },
      heartbeatFresh.ageMs === null ? null : fixed(heartbeatFresh.ageMs, 0),
      'neutral',
      state,
      now,
    ),
  ];
}

/** 0..360 归一化。`ATTITUDE.yaw` 是 ±180 的弧度换算结果，可能为负。 */
export function normalizeHeading(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

export interface AttitudeModel {
  readonly rollDeg: number | null;
  readonly pitchDeg: number | null;
  readonly headingDeg: number | null;
  /** 能不能画姿态球。任一为 `null` 就不画 —— 缺一个轴画出来的"水平"是编的 */
  readonly drawable: boolean;
  readonly stale: boolean;
  readonly ageLabel: string;
  readonly headingStale: boolean;
  readonly headingAgeLabel: string;
}

export function attitudeOf(state: VehicleState, now: number): AttitudeModel {
  const attitude = freshnessOf(state, 'attitude', now);
  const position = freshnessOf(state, 'position', now);
  const headingDeg = state.headingDeg ?? (state.yawDeg === null ? null : normalizeHeading(state.yawDeg));

  return {
    rollDeg: state.rollDeg,
    pitchDeg: state.pitchDeg,
    headingDeg,
    drawable: state.rollDeg !== null && state.pitchDeg !== null,
    stale: attitude.stale,
    ageLabel: attitude.ageLabel,
    headingStale: position.stale,
    headingAgeLabel: position.ageLabel,
  };
}

/* ────────────────────── 俯仰梯与航向带刻度 ────────────────────── */

export interface PitchMark {
  readonly deg: number;
  /** 每 10 度一条长线，5 度是短线 */
  readonly major: boolean;
  readonly label: string | null;
}

/** 俯仰梯：-90..90 每 5 度一条线，每 10 度一个数字。刻度是常量，与当前姿态无关。 */
export function pitchMarks(): readonly PitchMark[] {
  const marks: PitchMark[] = [];
  for (let deg = -90; deg <= 90; deg += 5) {
    const major = deg % 10 === 0;
    marks.push({ deg, major, label: major && deg !== 0 ? String(deg) : null });
  }
  return marks;
}

export interface HeadingTick {
  readonly deg: number;
  readonly major: boolean;
  /** 相对中心的位置，-1（最左）…0（正中）…1（最右） */
  readonly offset: number;
  readonly label: string | null;
}

const COMPASS_LABELS: ReadonlyMap<number, string> = new Map([
  [0, '北'],
  [90, '东'],
  [180, '南'],
  [270, '西'],
]);

/** 两个航向之间的最短角差，结果落在 (-180, 180]。直接相减会在跨越 0/360 时把刻度整段带偏。 */
export function shortestHeadingDelta(target: number, reference: number): number {
  return ((target - reference + 540) % 360) - 180;
}

/**
 * 航向最近的方位词（北/东/南/西），只在基本对准时给。
 *
 * 给一个"307° → 西北"的近似读数会把 22.5° 的扇区说成"正西"，而飞行员会把它当方向用；
 * 所以只在离正方位 8° 以内才返回，其余情况返回 `null`（界面上就只显示数字）。
 */
export function cardinalLabel(headingDeg: number): string | null {
  const nearest = normalizeHeading(Math.round(headingDeg / 90) * 90);
  const label = COMPASS_LABELS.get(nearest);
  if (label === undefined) return null;
  return Math.abs(shortestHeadingDelta(normalizeHeading(headingDeg), nearest)) <= 8 ? label : null;
}

export function headingTicks(headingDeg: number, halfSpanDeg = 60, stepDeg = 5): readonly HeadingTick[] {
  const ticks: HeadingTick[] = [];
  const start = Math.ceil((headingDeg - halfSpanDeg) / stepDeg) * stepDeg;
  for (let raw = start; raw <= headingDeg + halfSpanDeg; raw += stepDeg) {
    const deg = normalizeHeading(raw);
    const delta = shortestHeadingDelta(deg, headingDeg);
    if (Math.abs(delta) > halfSpanDeg) continue;
    const major = deg % 30 === 0;
    ticks.push({
      deg,
      major,
      offset: delta / halfSpanDeg,
      label: major ? (COMPASS_LABELS.get(deg) ?? String(deg)) : null,
    });
  }
  return ticks;
}

/* ────────────────────────────── 仪表 ────────────────────────────── */

export type ItemFreshness = 'fresh' | 'stale' | 'missing';

export interface InstrumentItem {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly level: StatusLevel;
  readonly freshness: ItemFreshness;
  /** "为什么这个值能/不能信"；不复述这一项是什么 */
  readonly note: string;
}

export interface InstrumentSection {
  readonly id: string;
  readonly title: string;
  readonly level: StatusLevel;
  /** 一句话结论。异常时它就是那条"醒目的文字"，而不是靠一个淡灰色小点 */
  readonly summary: string;
  readonly items: readonly InstrumentItem[];
}

interface ItemSpec {
  readonly id: string;
  readonly label: string;
  readonly unit: string;
  readonly source: string;
  readonly key: TelemetryKey;
  /**
   * 数值型读数。`null` = 这一项没有数据。
   *
   * 这里**故意不做格式化**：把 `null` 提前变成 `'--'` 会让"没有数据"和"值是 0"
   * 在下游再也分不开 —— 而那正是这个面板存在的意义。
   */
  readonly read?: (state: VehicleState, stats: LinkStats | null) => number | null;
  readonly digits?: number;
  /** 文本型读数（增/减量、枚举名、多轴合成值）。同样用 `null` 表示没有数据。 */
  readonly text?: (state: VehicleState, stats: LinkStats | null) => string | null;
  readonly level?: (state: VehicleState, stats: LinkStats | null) => StatusLevel;
}

function rawValueOf(spec: ItemSpec, state: VehicleState, stats: LinkStats | null): string | null {
  if (spec.text !== undefined) return spec.text(state, stats);
  const value = spec.read === undefined ? null : spec.read(state, stats);
  if (value === null) return null;
  return fixed(value, spec.digits ?? 0);
}

function buildItem(spec: ItemSpec, state: VehicleState, stats: LinkStats | null, now: number): InstrumentItem {
  const freshness = freshnessOf(state, spec.key, now);
  const raw = rawValueOf(spec, state, stats);
  const missing = raw === null;
  const base = spec.level?.(state, stats) ?? 'neutral';

  const note = missing
    ? `没有数据：还没收到过 ${spec.source}`
    : freshness.stale
      ? `已过期：${freshness.ageLabel}更新（${spec.source}）`
      : `来自 ${spec.source}`;

  return {
    id: spec.id,
    label: spec.label,
    value: raw ?? NO_DATA,
    unit: missing ? '' : spec.unit,
    level: freshness.stale && !missing ? worstLevel(base, 'caution') : base,
    freshness: missing ? 'missing' : freshness.stale ? 'stale' : 'fresh',
    note,
  };
}

function buildSection(
  id: string,
  title: string,
  specs: readonly ItemSpec[],
  state: VehicleState,
  stats: LinkStats | null,
  now: number,
  summarize: (items: readonly InstrumentItem[]) => { summary: string; level: StatusLevel },
  extra: readonly InstrumentItem[] = [],
): InstrumentSection {
  const items = [...specs.map((spec) => buildItem(spec, state, stats, now)), ...extra];
  const everyMissing = items.every((item) => item.freshness === 'missing');
  const { summary, level } = summarize(items);

  return {
    id,
    title,
    items,
    summary: everyMissing ? `${title}：没有任何数据（相关消息一条都还没收到）` : summary,
    level: everyMissing ? worstLevel(level, 'warning') : level,
  };
}

/** 剩余电量分级：越过 25% 就该提醒，越过 10% 已经不是提醒而是"马上落地"。 */
function batteryLevel(remainingPct: number | null): StatusLevel {
  if (remainingPct === null) return 'neutral';
  if (remainingPct <= 10) return 'critical';
  if (remainingPct <= 25) return 'warning';
  return 'ok';
}

const BATTERY_SPECS: readonly ItemSpec[] = [
  { id: 'voltage', label: '电压', unit: 'V', source: 'SYS_STATUS', key: 'battery', read: (s) => s.voltageV, digits: 2 },
  { id: 'current', label: '电流', unit: 'A', source: 'SYS_STATUS', key: 'battery', read: (s) => s.currentA, digits: 2 },
  {
    id: 'remaining',
    label: '剩余电量',
    unit: '%',
    source: 'BATTERY_STATUS',
    key: 'battery',
    read: (s) => s.remainingPct,
    digits: 0,
    level: (s) => batteryLevel(s.remainingPct),
  },
];

/** `MAV_GPS_FIX_TYPE`：0/1 = 无定位，2 = 2D（拿不到高度也稳不住位置），3 = 3D，4+ = 差分/RTK */
function fixLevel(fixType: number | null): StatusLevel {
  if (fixType === null) return 'neutral';
  if (fixType === 2) return 'warning';
  if (fixType >= 3) return 'ok';
  return 'critical';
}

const GPS_SPECS: readonly ItemSpec[] = [
  {
    id: 'fix',
    label: '定位类型',
    unit: '',
    source: 'GPS_RAW_INT',
    key: 'gps',
    text: (s) => s.fixTypeName?.replace('GPS_FIX_TYPE_', '') ?? (s.fixType === null ? null : `类型 ${s.fixType}`),
    level: (s) => fixLevel(s.fixType),
  },
  {
    id: 'satellites',
    label: '可见卫星',
    unit: '颗',
    source: 'GPS_RAW_INT',
    key: 'gps',
    read: (s) => s.satellites,
    digits: 0,
    // 6 颗是 ArduPilot 默认的解锁门限：低于它连解锁都会被拒
    level: (s) => (s.satellites === null ? 'neutral' : s.satellites < 6 ? 'warning' : 'ok'),
  },
  {
    id: 'hdop',
    label: '水平精度',
    unit: 'HDOP',
    source: 'GPS_RAW_INT',
    key: 'gps',
    read: (s) => s.hdop,
    digits: 2,
    level: (s) => (s.hdop === null ? 'neutral' : s.hdop > 2 ? 'caution' : 'ok'),
  },
];

function vibrationLevel(vibration: VehicleState['vibration']): StatusLevel {
  if (vibration === null) return 'neutral';
  if (vibration.clipping !== null && vibration.clipping > 0) return 'warning';
  const peak = Math.max(vibration.x ?? 0, vibration.y ?? 0, vibration.z ?? 0);
  if (peak >= 30) return 'warning';
  if (peak >= 15) return 'caution';
  return 'ok';
}

const ESTIMATOR_SPECS: readonly ItemSpec[] = [
  {
    id: 'ekf',
    label: 'EKF 健康',
    unit: '',
    source: 'EKF_STATUS_REPORT',
    key: 'ekf',
    text: (s) => (s.ekfHealthy === null ? null : s.ekfHealthy ? '正常' : '异常'),
    level: (s) => (s.ekfHealthy === null ? 'neutral' : s.ekfHealthy ? 'ok' : 'critical'),
  },
  {
    id: 'ekf-horiz',
    label: '水平位置方差',
    unit: '',
    source: 'EKF_STATUS_REPORT',
    key: 'ekf',
    read: (s) => s.ekfPosHorizVariance,
    digits: 3,
  },
  {
    id: 'ekf-vert',
    label: '垂直位置方差',
    unit: '',
    source: 'EKF_STATUS_REPORT',
    key: 'ekf',
    read: (s) => s.ekfPosVertVariance,
    digits: 3,
  },
  {
    id: 'vibration',
    label: '振动 X / Y / Z',
    unit: 'm/s²',
    source: 'VIBRATION',
    key: 'vibration',
    text: (s) =>
      s.vibration === null
        ? null
        : `${fixed(s.vibration.x, 1)} / ${fixed(s.vibration.y, 1)} / ${fixed(s.vibration.z, 1)}`,
    level: (s) => vibrationLevel(s.vibration),
  },
  {
    id: 'clipping',
    label: '削波次数',
    unit: '',
    source: 'VIBRATION',
    key: 'vibration',
    read: (s) => s.vibration?.clipping ?? null,
    digits: 0,
    // 削波不为 0 说明振动已经打到加速度计量程，读数本身不再可信
    level: (s) =>
      s.vibration === null || s.vibration.clipping === null
        ? 'neutral'
        : s.vibration.clipping > 0
          ? 'warning'
          : 'ok',
  },
];

/**
 * 解析器错误占"收到帧 + 错误"的比例。
 *
 * 为什么不是"丢包率"：MAVLink 的帧头里**没有序号**，地面站无法判断对端发了几帧、
 * 自己丢了几帧。能实测的只有"收到的字节里有多少解不开"，所以这里如实标注成错误率，
 * 而不是编一个看起来更漂亮的丢包数字。
 */
function errorRatePct(stats: LinkStats): number {
  const broken = stats.crcErrors + stats.malformedFrames + stats.signatureFailures;
  const total = stats.framesIn + broken;
  return total === 0 ? 0 : (broken / total) * 100;
}

export function buildInstruments(
  state: VehicleState,
  stats: LinkStats | null,
  now: number,
): readonly InstrumentSection[] {
  // 链路质量的观测对象是"会话"，不是车辆；它自己的数据源是 stats，不是 state
  const linkSpecs: readonly ItemSpec[] = [
    {
      id: 'rate',
      label: '帧速率',
      unit: 'Hz',
      source: '会话统计',
      key: 'heartbeat',
      read: (_state, linkStats) => (linkStats === null ? null : linkStats.framesPerSecond),
      digits: 1,
    },
    {
      id: 'throughput',
      label: '字节速率',
      unit: 'KiB/s',
      source: '会话统计',
      key: 'heartbeat',
      read: (_state, linkStats) => (linkStats === null ? null : linkStats.bytesPerSecond / 1024),
      digits: 1,
    },
    {
      id: 'latency',
      label: '时延',
      unit: 'ms',
      source: '会话统计',
      key: 'heartbeat',
      read: (_state, linkStats) =>
        linkStats === null || linkStats.silenceMs === null ? null : linkStats.silenceMs,
      digits: 0,
      level: (_state, linkStats) => (linkStats?.silenceMs != null && linkStats.silenceMs > 3000 ? 'warning' : 'ok'),
    },
    {
      id: 'error-rate',
      label: '错误率',
      unit: '%',
      source: '会话统计',
      key: 'heartbeat',
      read: (_state, linkStats) => (linkStats === null ? null : errorRatePct(linkStats)),
      digits: 3,
      level: (_state, linkStats) => (linkStats !== null && errorRatePct(linkStats) > 1 ? 'warning' : 'ok'),
    },
  ];

  return [
    buildSection('battery', '电池', BATTERY_SPECS, state, stats, now, (items) => {
      const remaining = items.find((item) => item.id === 'remaining');
      const level = items.reduce<StatusLevel>((worst, item) => worstLevel(worst, item.level), 'neutral');
      const text = remaining?.value ?? NO_DATA;
      return {
        summary:
          remaining?.freshness === 'missing'
            ? '电池：没有剩余电量数据，不要凭电压猜测续航'
            : level === 'critical'
              ? `电量告急：剩余 ${text}%，请立即降落`
              : level === 'warning'
                ? `电量偏低：剩余 ${text}%，应尽快返航`
                : `电池正常：剩余 ${text}%`,
        level,
      };
    }),
    buildSection('gps', 'GPS', GPS_SPECS, state, stats, now, (items) => {
      const fix = items.find((item) => item.id === 'fix');
      const level = items.reduce<StatusLevel>((worst, item) => worstLevel(worst, item.level), 'neutral');
      return {
        summary:
          fix?.freshness === 'missing'
            ? 'GPS：没有定位数据'
            : level === 'critical'
              ? '定位不可用：连 2D 定位都没有，任何依赖位置的操作都不能做'
              : level === 'warning'
                ? `定位不可靠：${fix?.value ?? NO_DATA}，不要依赖它自动飞行`
                : `定位可用：${fix?.value ?? NO_DATA}`,
        level,
      };
    }),
    buildSection('estimator', '估计器与振动', ESTIMATOR_SPECS, state, stats, now, (items) => {
      const ekf = items.find((item) => item.id === 'ekf');
      const clipping = items.find((item) => item.id === 'clipping');
      const level = items.reduce<StatusLevel>((worst, item) => worstLevel(worst, item.level), 'neutral');
      return {
        summary:
          ekf?.freshness === 'missing'
            ? '估计器：没有 EKF 状态数据'
            : ekf?.value === '异常'
              ? 'EKF 报异常：姿态与位置估计不可信，立刻停止依赖自动模式的操作'
              : clipping !== undefined && clipping.value !== NO_DATA && clipping.value !== '0'
                ? `振动削波 ${clipping.value} 次：加速度计已经打到量程，读数不可信`
                : '估计器与振动正常',
        level,
      };
    }),
    buildSection(
      'link',
      '链路质量',
      linkSpecs,
      state,
      stats,
      now,
      (items) => {
        const latency = items.find((item) => item.id === 'latency');
        const level = items.reduce<StatusLevel>((worst, item) => worstLevel(worst, item.level), 'neutral');
        return {
          summary:
            stats === null
              ? '链路：还没有建立连接'
              : level === 'warning'
                ? `链路静默：最后一帧在 ${latency?.value ?? NO_DATA} 毫秒前，可能已经断开`
                : `链路正常：收帧 ${items.find((item) => item.id === 'rate')?.value ?? NO_DATA} Hz`,
          level,
        };
      },
      [heartbeatAgeItem(state, now)],
    ),
  ];
}

/** 心跳年龄是"链路质量"里唯一由车辆状态算出来的量（其余来自会话统计），单独构造一条。 */
export function heartbeatAgeItem(state: VehicleState, now: number): InstrumentItem {
  const freshness = freshnessOf(state, 'heartbeat', now);
  const missing = freshness.ageMs === null;
  return {
    id: 'heartbeat-age',
    label: '心跳年龄',
    unit: missing ? '' : 'ms',
    value: missing ? NO_DATA : fixed(freshness.ageMs, 0),
    level: freshness.stale ? 'warning' : 'ok',
    freshness: missing ? 'missing' : freshness.stale ? 'stale' : 'fresh',
    note: missing
      ? '没有数据：还没收到过 HEARTBEAT'
      : `来自 HEARTBEAT（${freshness.ageLabel}）`,
  };
}

/**
 * 一条命令的结论，供界面显示。
 *
 * `text` 必须是**具体原因**：命令管理器会把 ACK 前后飞控用 `STATUSTEXT` 播报的那句话
 * 拼进来（"执行失败：PreArm: GPS 卫星数不足"）。只显示"失败"两个字，等于把
 * 排障需要的信息丢了，而那条信息是飞控唯一一次说清原因的机会。
 */
export interface CommandFeedback {
  readonly level: StatusLevel;
  readonly title: string;
  readonly text: string;
}
